import { unsetRating } from './access';
import type { Category, Id, Kink, KinklistDoc } from './types';

export interface Progress {
  rated: number;
  total: number;
}

/** A kink counts as rated once every one of its roles has a rating other than "unset". */
export function isKinkRated(kink: Kink, unsetId: Id): boolean {
  return Object.values(kink.ratings).every((ratingId) => ratingId !== unsetId);
}

/**
 * Progress counts individual choices (kink × role), so every rating moves it;
 * counting whole kinks made the bar look stuck after rating one of two roles.
 */
export function categoryProgress(category: Category, unsetId: Id): Progress {
  let rated = 0;
  for (const kink of category.kinks) {
    for (const role of category.roles) if ((kink.ratings[role.id] ?? unsetId) !== unsetId) rated++;
  }
  return { rated, total: category.kinks.length * category.roles.length };
}

export function docProgress(doc: KinklistDoc): Progress {
  const unsetId = unsetRating(doc.ratings).id;
  return doc.categories.reduce(
    (sum, category) => {
      const { rated, total } = categoryProgress(category, unsetId);
      return { rated: sum.rated + rated, total: sum.total + total };
    },
    { rated: 0, total: 0 },
  );
}

/** Whether the user has rated or commented anything (used to confirm before replacing). */
export function hasUserInput(doc: KinklistDoc): boolean {
  const unsetId = unsetRating(doc.ratings).id;
  return doc.categories.some((category) =>
    category.kinks.some(
      (kink) =>
        kink.comment !== undefined ||
        Object.values(kink.ratings).some((ratingId) => ratingId !== unsetId),
    ),
  );
}
